import {
    createProvider,
    getAllProviders,
    setProviderPermission,
    updateProviderConfiguration,
} from './http/labProviders';
import type { ProviderConfigurationUpdate } from './http/labProviders';
import type { FetchQueryOptions, MutationOptions } from '@tanstack/query-core';
import type { ProviderAccessRequest } from '@thinkalpha/locates-ws-client';
import type { ProviderPermissionMutationPayload } from 'src/admin/LAB/contracts/permissions';
import type { Provider, ProviderInstruments } from 'src/admin/LAB/contracts/providers';
import type { ReactBindings } from 'src/types/bindings';

/**
 * QueryKey Constants
 */
export const LAB_PROVIDERS = 'lab-providers';
export const LAB_PROVIDER_REBATE_BASE = 'lab-provider-rebate';

/**
 * Query for retrieving providers the caller has access to. When `firmId` is provided the backend
 * narrows the list to providers that firm owns (PLAT-12660), so callers with access to multiple
 * firms only see the viewed firm's providers. `firmId` is included in the query key so switching
 * firms does not serve stale cached data. Toggling superuser does not need a key dimension here:
 * QueryCacheUserService resets all user-scoped queries when the effective identity changes.
 */
export const getAllProvidersQuery = (
    firmId?: string,
): FetchQueryOptions<Awaited<ReturnType<typeof getAllProviders>>> => {
    return {
        queryKey: firmId ? [LAB_PROVIDERS, 'list', 'firm', firmId] : [LAB_PROVIDERS, 'list'],
        queryFn: () => getAllProviders(firmId),
    };
};

/**
 * Mutation to create a new provider.
 */
export const createProviderMutation = (): MutationOptions<
    Awaited<ReturnType<typeof createProvider>>,
    Error,
    ProviderAccessRequest
> => {
    return {
        mutationFn: createProvider,
    };
};

/**
 * Mutation for setting an existing providers permissions.
 */
export const setProviderPermissionMutation = (): MutationOptions<
    Awaited<ReturnType<typeof setProviderPermission>>,
    Error,
    ProviderPermissionMutationPayload
> => {
    return {
        mutationFn: setProviderPermission,
    };
};

export const updateProviderConfigurationMutation = (): MutationOptions<
    Awaited<ReturnType<typeof updateProviderConfiguration>>,
    Error,
    ProviderConfigurationUpdate
> => {
    return {
        mutationFn: updateProviderConfiguration,
    };
};

// Models may not import `queries/http/**`; the instrument writes are re-exported here.
export { addProviderInstrument, removeProviderInstrument } from './http/labProviders';

/**
 * Replace one provider's `instruments` map in every cached provider list, so the page reflects a
 * write immediately without waiting for the invalidation refetch.
 */
export const setCachedProviderInstruments = (
    queryClient: ReactBindings['QueryClient'],
    providerId: string,
    instruments: ProviderInstruments,
) => {
    queryClient.setQueriesData<Provider[]>({ queryKey: [LAB_PROVIDERS, 'list'] }, (providers) =>
        providers?.map((provider) => (provider.id === providerId ? { ...provider, instruments } : provider)),
    );
};

/**
 * Invalidation for providers query.
 */
export const invalidateProvidersQuery = (
    queryClient: ReactBindings['QueryClient'],
    providerId: string | undefined = undefined,
) => {
    return providerId
        ? queryClient.invalidateQueries({ queryKey: [LAB_PROVIDERS, providerId] })
        : queryClient.invalidateQueries({ queryKey: [LAB_PROVIDERS] });
};
